/* eslint-disable jsx-a11y/anchor-is-valid */
import clsx from 'clsx';
import React, { FC, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { KTIcon } from '../../../helpers';
import { get_all_unread_notifications, get_read_notification } from './api/notificationApi';
import { useQuery, useMutation, useQueryClient } from 'react-query';

interface NotificationItem {
    id: number | string;
    notification_title: string;
    notification_description: NotificationDescription;
    notification_type: string;
    data: {
        leave_id?: number;
        status?: string;
        from_date?: string;
        to_date?: string;
        approval_message?: string | null;
        [key: string]: any;
    };
    read_at: string | null;
    created_at: string;
    updated_at: string;
}
export interface AppointmentSlot {
    datetime: string;
}

export interface NotificationDetails {
    appointment_date_time_slots?: AppointmentSlot[];
}

export interface NotificationDescription {
    customer_name: string;
    people_name: string;
    details?: NotificationDetails;
}
const formatDateTime = (dateTimeString: string | null): string => {
    if (!dateTimeString) return 'N/A';
    try {
        return new Date(dateTimeString).toLocaleString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch (e) {
        console.error("Error formatting date:", e, "Input:", dateTimeString);
        return dateTimeString || 'Invalid Date';
    }
};

const unreadNotificationsQueryKey = ['get_all_unread_notifications'];

type Notifications = {
    id: number;
    notification_title: string | undefined;
    notification_description: number | undefined;
    notification_type: string | undefined;
    updated_by: string | undefined;
    created_at: string;
};
export const HeaderNotificationsMenu = ({ setUnreadCount }: { setUnreadCount: (count: number) => void }) => {
    const [unreadNotificationsList, setUnreadNotificationsList] = useState<Notifications[]>([]);
    const queryClient = useQueryClient();
    const isPeople = !!(JSON.parse(localStorage.getItem('kt-auth-react-v') || '{}')?.user?.is_people ?? false)
    const unreadNotificationsQuery = useQuery(
        unreadNotificationsQueryKey,
        () => get_all_unread_notifications(isPeople),
        {
            refetchInterval: 5000,
            staleTime: 0,
            select: (data) => {
                if (Array.isArray(data?.data)) {
                    return {
                        originalData: data.data,
                        data: data.data.slice(0, 5),
                    };
                }
                return {
                    originalData: [],
                    data: [],
                };
            },
        }
    )
    useEffect(() => {
        if (unreadNotificationsQuery.data?.originalData) {
            setUnreadCount(unreadNotificationsQuery.data.originalData.length);
        }
    }, [unreadNotificationsQuery.data?.originalData]);
    const readNotificationMutation = useMutation(
        (notificationId: number | string) => get_read_notification(notificationId, isPeople),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(unreadNotificationsQueryKey);
            },
            onError: (error: any) => {
                console.error('Failed to mark notification as read:', error?.message || error)
            },
        }
    )
    const handleNotificationClick = (notification: NotificationItem) => {
        if (!notification.read_at) {
            readNotificationMutation.mutate(notification.id);
        }
    };

    const renderNotificationContent = (notification: NotificationItem) => {
        const { data = {}, notification_title, notification_description } = notification as any;
        const isRead = !!notification.read_at;
        const isMarkingAsRead = readNotificationMutation.isLoading && readNotificationMutation.variables === notification.id;

        const itemClasses = clsx(
            "list-group-item list-group-item-action",
            "d-flex w-100 justify-content-between align-items-start",
            "py-3 px-3 px-md-4",
            {
                "bg-light-primary": !isRead && !isMarkingAsRead, // ⬅️ highlight unread
                "opacity-75": isRead && !isMarkingAsRead,
                "bg-light": isMarkingAsRead,
                "pe-none": isMarkingAsRead,
            }
        );

        const title = notification_title || 'New Notification';
        // const description = notification_description?.description || 'No description provided';
        const timeSlots = notification.notification_description?.details?.appointment_date_time_slots;
        const customerName = notification.notification_description?.customer_name;
        const peopleName = notification.notification_description?.people_name;

        let message = 'No description provided';
        if (customerName && peopleName) {
            message = isPeople
                ? `${customerName} has booked a new appointment with you.`
                : `${customerName} has booked a new appointment with ${peopleName}.`;
        }



        return (
            <li
                key={notification.id}
                className={itemClasses}
                onClick={() => handleNotificationClick(notification)}
                style={{ cursor: 'pointer' }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') handleNotificationClick(notification);
                }}
            >
                <div className="flex-grow-1 me-3">
                    <h5 className="mb-1 fw-semibold d-flex align-items-center">
                        {title}
                        {isRead && <span className="badge bg-light text-dark border fw-normal ms-2 small">Read</span>}
                    </h5>
                    <p className="mb-1 small">{message}</p>

                    {Array.isArray(timeSlots) && timeSlots.length > 0 && (
                        <ul className="mb-1 small ps-3">
                            {timeSlots.map((slot, index) => (
                                <li key={index}>{formatDateTime(slot.datetime)}</li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="text-end text-nowrap d-flex flex-column align-items-end">
                    <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                        {isRead ? `Read: ${formatDateTime(notification.read_at)}` : `Received: ${formatDateTime(notification.created_at)}`}
                    </small>
                    {isMarkingAsRead && (
                        <div className="spinner-border spinner-border-sm text-primary mt-1" role="status">
                            <span className="visually-hidden">Marking as read...</span>
                        </div>
                    )}
                </div>
            </li>
        );
    };


    const displayedNotifications = unreadNotificationsQuery.data?.data || [];
    const totalUnreadCount = unreadNotificationsQuery.data?.originalData?.length || 0;
    const showViewAll = true;
    const handleFakeOutsideClick = () => {
        document.body.click();
    };
    return (
        <div
            className='menu menu-sub menu-sub-dropdown menu-column w-350px w-lg-400px'
            data-kt-menu='true'
            id="kt_header_notifications_menu"
        >
            <div
                className='d-flex flex-column bgi-no-repeat rounded-top px-9 pt-10 pb-2'
                style={{ backgroundColor: '#1E1E2D' }}
            >
                <h3 className='text-white fw-semibold mb-3'>
                    Notifications
                    {totalUnreadCount > 0 && (
                        <span className='badge bg-primary badge-circle fw-bold fs-8 ms-2'>
                            {totalUnreadCount}
                        </span>
                    )}
                </h3>
            </div>

            <div className="tab-content">
                <div className='tab-pane fade show active'>
                    <div className='scroll-y mh-350px my-2 px-2'>
                        {unreadNotificationsQuery.isLoading && (
                            <div className="d-flex justify-content-center align-items-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading notifications...</span>
                                </div>
                            </div>
                        )}

                        {unreadNotificationsQuery.isError && (
                            <div className="alert alert-danger mx-5 my-5">
                                Error loading notifications: {unreadNotificationsQuery.error instanceof Error ?
                                    unreadNotificationsQuery.error.message : 'Unknown error'}
                            </div>
                        )}

                        {!unreadNotificationsQuery.isLoading &&
                            !unreadNotificationsQuery.isError &&
                            displayedNotifications.length === 0 && (
                                <div className="text-center text-muted py-10 px-5">
                                    <KTIcon iconName='check-circle' className='fs-4tx text-success mb-5' />
                                    <div className='fw-semibold fs-4'>All caught up!</div>
                                    <div className='text-gray-500 fs-7'>You have no unread notifications.</div>
                                </div>
                            )}

                        {!unreadNotificationsQuery.isLoading &&
                            !unreadNotificationsQuery.isError &&
                            displayedNotifications.length > 0 && (
                                <ul className="list-group list-group-flush">
                                    {displayedNotifications.map(notification =>
                                        renderNotificationContent(notification)
                                    )}
                                </ul>
                            )}
                    </div>

                    {showViewAll && (
                        <div className='py-3 text-center border-top'>
                            <Link
                                to='/notifications'
                                className='btn btn-color-gray-600 btn-active-color-primary'
                                onClick={handleFakeOutsideClick}
                            >
                                View All Notifications <KTIcon iconName='arrow-right' className='fs-5' />
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
